/**
 * Runtime Monitor - Analyzes live project state to detect issues,
 * anti-patterns, and health signals across auth, database, routing,
 * forms, performance, and runtime errors.
 */

// ✅ FIX: useProjectStore is Zustand - browser-only.
// Replaced with server-safe getProjectState() wrapper.
// This is the root file pulling projectStore into the server build.

/* =========================================================
   🔥 SERVER-SAFE PROJECT STATE
   ========================================================= */

interface ProjectState {
  pages:     string[];
  schema:    any[];
  workflows: any[];
  logs:      any[];
  [key: string]: any;
}

function getProjectState(): ProjectState {
  if (typeof window !== "undefined") {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { useProjectStore } = require("@/store/projectStore");
      return useProjectStore.getState() as ProjectState;
    } catch { /* fall through */ }
  }
  return { pages: [], schema: [], workflows: [], logs: [] };
}

import { logger } from "@/system/logger";

/* =========================================================
   🧠 TYPES
   ========================================================= */

export type IssueSeverity = "critical" | "major" | "minor" | "info";

export type IssueCategory =
  | "form"
  | "api"
  | "auth"
  | "database"
  | "routing"
  | "ui"
  | "performance"
  | "build"
  | "runtime"
  | "dependency"
  | "security"
  | "compliance";

export interface DetectedIssue {
  id:           string;
  category:     IssueCategory;
  severity:     IssueSeverity;
  title:        string;
  description:  string;
  rootCause:    string;
  suggestedFix: string;
  autoFixable:  boolean;
  detectedAt:   number;
}

export interface MonitorReport {
  issues:       DetectedIssue[];
  totalIssues:  number;
  criticalCount: number;
  majorCount:   number;
  healthScore:  number;
  generatedAt:  number;
}

/* =========================================================
   STATE
   ========================================================= */

let issueCounter = 0;

/* =========================================================
   UTILS
   ========================================================= */

function pushIssue(
  list: DetectedIssue[],
  seen: Set<string>,
  issue: DetectedIssue
): void {
  if (seen.has(issue.title)) return;
  seen.add(issue.title);
  list.push(issue);
}

function severityRank(sev: IssueSeverity): number {
  return { critical: 4, major: 3, minor: 2, info: 1 }[sev];
}

function createIssue(
  category:     IssueCategory,
  severity:     IssueSeverity,
  title:        string,
  description:  string,
  rootCause:    string,
  suggestedFix: string,
  autoFixable:  boolean
): DetectedIssue {
  return {
    id:           `issue-${++issueCounter}-${Date.now()}`,
    category,
    severity,
    title,
    description,
    rootCause,
    suggestedFix,
    autoFixable,
    detectedAt:   Date.now(),
  };
}

function computeHealthScore(issues: DetectedIssue[]): number {
  if (issues.length === 0) return 100;
  const deductions = issues.reduce((sum, i) => {
    return sum + { critical: 25, major: 10, minor: 3, info: 0 }[i.severity];
  }, 0);
  return Math.max(0, 100 - deductions);
}

/* =========================================================
   CHECK MODULES
   ========================================================= */

function checkAuth(
  pages:  string[],
  schema: any[],
  issues: DetectedIssue[],
  seen:   Set<string>
): void {
  const hasLoginPage  = pages.some(p => /login|auth|sign/i.test(String(p)));
  const hasUserModel  = schema.some(m => /user|account/i.test(String(m?.name)));
  const hasRoleModel  = schema.some(m => /role|permission/i.test(String(m?.name)));

  if (hasLoginPage && !hasUserModel) {
    pushIssue(issues, seen, createIssue(
      "auth", "critical",
      "Missing user data model",
      "Login page exists but no User model defined",
      "Authentication cannot function without user schema",
      "Create User model with email, password, role fields",
      true
    ));
  }

  if (hasUserModel && !hasLoginPage) {
    pushIssue(issues, seen, createIssue(
      "auth", "major",
      "Missing authentication page",
      "User model exists but no login UI",
      "Users cannot access the system",
      "Generate login/signup page",
      true
    ));
  }

  if (hasUserModel && !hasRoleModel) {
    pushIssue(issues, seen, createIssue(
      "auth", "minor",
      "No role/permission model",
      "User model found but no role-based access control model",
      "All users will have equal access - no authorization layer",
      "Add Role model with permissions field",
      true
    ));
  }
}

function checkDatabase(
  schema: any[],
  issues: DetectedIssue[],
  seen:   Set<string>
): void {
  schema.forEach((model: any) => {
    if (!model) return;

    if (!model.fields || model.fields.length === 0) {
      pushIssue(issues, seen, createIssue(
        "database", "major",
        `Empty model: ${model.name}`,
        `Model "${model.name}" has no fields`,
        "Cannot store data without fields",
        `Add fields to "${model.name}"`,
        false
      ));
    }

    const hasId = model.fields?.some((f: any) => /^id$/i.test(f?.name));
    if (!hasId && model.fields?.length > 0) {
      pushIssue(issues, seen, createIssue(
        "database", "minor",
        `Missing ID: ${model.name}`,
        `Model "${model.name}" lacks a primary ID field`,
        "Records cannot be uniquely identified",
        `Add UUID id field to "${model.name}"`,
        true
      ));
    }

    const hasCreatedAt = model.fields?.some((f: any) => /created_at|createdAt/i.test(f?.name));
    if (!hasCreatedAt && model.fields?.length > 0) {
      pushIssue(issues, seen, createIssue(
        "database", "info",
        `Missing timestamps: ${model.name}`,
        `Model "${model.name}" has no created_at timestamp`,
        "Audit trail and sorting by creation time will be unavailable",
        `Add created_at and updated_at fields to "${model.name}"`,
        true
      ));
    }
  });
}

function checkRouting(
  pages:  string[],
  issues: DetectedIssue[],
  seen:   Set<string>
): void {
  if (pages.length === 0) {
    pushIssue(issues, seen, createIssue(
      "routing", "critical",
      "No pages defined",
      "Project has no pages at all",
      "Application has no UI to render",
      "Add at least a Home/Dashboard page",
      true
    ));
    return;
  }

  if (pages.length === 1) {
    pushIssue(issues, seen, createIssue(
      "routing", "info",
      "Single page app",
      "Only one page exists",
      "Limited navigation may reduce usability",
      "Consider adding more pages for better UX",
      false
    ));
  }

  const hasDashboard = pages.some(p => /dashboard|home|main/i.test(String(p)));
  const hasError     = pages.some(p => /404|error|not.?found/i.test(String(p)));

  if (!hasDashboard && pages.length > 2) {
    pushIssue(issues, seen, createIssue(
      "routing", "minor",
      "No dashboard/home page",
      "No central landing page detected",
      "Users have no clear entry point after login",
      "Add a Dashboard or Home page",
      true
    ));
  }

  if (!hasError && pages.length > 3) {
    pushIssue(issues, seen, createIssue(
      "routing", "info",
      "Missing 404 error page",
      "No error/not-found page detected",
      "Users will see blank screen on invalid routes",
      "Add a 404 Not Found page",
      true
    ));
  }
}

function checkForms(
  pages:     string[],
  workflows: any[],
  issues:    DetectedIssue[],
  seen:      Set<string>
): void {
  const formPages = pages.filter(p => /form|contact|submit|register/i.test(String(p)));

  formPages.forEach(page => {
    const hasWorkflow = workflows.some((w: any) =>
      new RegExp(String(page), "i").test(String(w))
    );

    if (!hasWorkflow) {
      pushIssue(issues, seen, createIssue(
        "form", "major",
        `No workflow: ${page}`,
        `Form page "${page}" has no submission handler`,
        "Form submissions will silently fail",
        `Create a workflow or API handler for "${page}"`,
        true
      ));
    }
  });
}

function checkPerformance(
  pages:  string[],
  schema: any[],
  issues: DetectedIssue[],
  seen:   Set<string>
): void {
  if (schema.length > 10) {
    pushIssue(issues, seen, createIssue(
      "performance", "minor",
      "Large schema detected",
      `${schema.length} models detected - may cause heavy queries`,
      "Unoptimized queries against large schemas slow response times",
      "Add database indexes and use pagination + lazy loading",
      false
    ));
  }

  if (pages.length > 15) {
    pushIssue(issues, seen, createIssue(
      "performance", "minor",
      "High page count",
      `${pages.length} pages detected`,
      "Large bundles increase initial load time",
      "Enable code splitting and lazy-load routes",
      false
    ));
  }

  if (schema.length > 5 && schema.every((m: any) => !m.fields?.some((f: any) => f?.indexed))) {
    pushIssue(issues, seen, createIssue(
      "performance", "minor",
      "No indexed fields detected",
      "None of your data models appear to have indexed fields",
      "Queries will perform full table scans at scale",
      "Add indexes to frequently-queried fields (email, userId, createdAt)",
      false
    ));
  }
}

function checkSecurity(
  pages:  string[],
  schema: any[],
  issues: DetectedIssue[],
  seen:   Set<string>
): void {
  const hasPaymentPage  = pages.some(p => /payment|checkout|billing/i.test(String(p)));
  const hasPasswordField = schema.some((m: any) =>
    m.fields?.some((f: any) => /password/i.test(f?.name))
  );

  if (hasPasswordField) {
    const hasHashedField = schema.some((m: any) =>
      m.fields?.some((f: any) => /hash|hashed/i.test(f?.name))
    );
    if (!hasHashedField) {
      pushIssue(issues, seen, createIssue(
        "security", "critical",
        "Plaintext password field detected",
        "A model has a 'password' field with no hashed equivalent",
        "Passwords stored in plaintext are a critical security vulnerability",
        "Use password_hash field and bcrypt/argon2 hashing - never store plaintext",
        false
      ));
    }
  }

  if (hasPaymentPage) {
    const hasSSL = pages.some(p => /https|ssl|secure/i.test(String(p)));
    if (!hasSSL) {
      pushIssue(issues, seen, createIssue(
        "security", "major",
        "Payment page without SSL indicators",
        "Payment or checkout page detected without SSL configuration",
        "Payment data transmitted without encryption is a PCI-DSS violation",
        "Ensure HTTPS is enforced and configure SSL certificates",
        false
      ));
    }
  }
}

function checkRuntimeLogs(
  logs:   any[],
  issues: DetectedIssue[],
  seen:   Set<string>
): void {
  logs.forEach((log: any) => {
    if (!log?.message) return;

    if (/cannot find module|failed to compile/i.test(log.message)) {
      pushIssue(issues, seen, createIssue(
        "build", "critical",
        "Build failure detected",
        log.message,
        "Project cannot compile - server/client will not start",
        "Check import paths and installed dependencies",
        true
      ));
    }

    if (/undefined|null is not|crash/i.test(log.message)) {
      pushIssue(issues, seen, createIssue(
        "runtime", "major",
        "Runtime crash detected",
        log.message,
        "Application is throwing unhandled exceptions",
        "Add null checks and error boundaries around affected components",
        true
      ));
    }

    if (/401|403|unauthorized|forbidden/i.test(log.message)) {
      pushIssue(issues, seen, createIssue(
        "auth", "major",
        "Auth error in logs",
        log.message,
        "Requests are failing authentication or authorization checks",
        "Verify JWT tokens, session expiry, and permission guards",
        false
      ));
    }

    if (/timeout|ETIMEDOUT|ECONNREFUSED/i.test(log.message)) {
      pushIssue(issues, seen, createIssue(
        "api", "major",
        "Network timeout detected",
        log.message,
        "External service or database is unreachable",
        "Check service health, add retry logic and circuit breakers",
        false
      ));
    }
  });
}

/* =========================================================
   🔥 CORE MONITOR
   ========================================================= */

export function runRuntimeMonitor(): DetectedIssue[] {
  // ✅ FIX: replaced useProjectStore.getState() with getProjectState()
  const state = getProjectState();

  const issues: DetectedIssue[] = [];
  const seen   = new Set<string>();
  issueCounter = 0;

  const pages     = state.pages     ?? [];
  const schema    = state.schema    ?? [];
  const workflows = state.workflows ?? [];
  const logs      = state.logs      ?? [];

  checkAuth(pages, schema, issues, seen);
  checkDatabase(schema, issues, seen);
  checkRouting(pages, issues, seen);
  checkForms(pages, workflows, issues, seen);
  checkPerformance(pages, schema, issues, seen);
  checkSecurity(pages, schema, issues, seen);
  checkRuntimeLogs(logs, issues, seen);

  // Fallback healthy state
  if (issues.length === 0) {
    issues.push(createIssue(
      "ui", "info",
      "System healthy",
      "No issues detected in current project state",
      "All systems operational",
      "Continue monitoring - nuvrahq.com for docs",
      false
    ));
  }

  issues.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));

  logger.info(`[RuntimeMonitor] ${issues.length} issues detected`, {
    critical: issues.filter(i => i.severity === "critical").length,
    major:    issues.filter(i => i.severity === "major").length,
  });

  return issues;
}

/**
 * Full monitor report with health score and counts.
 */
export function getMonitorReport(): MonitorReport {
  const issues = runRuntimeMonitor();
  return {
    issues,
    totalIssues:   issues.filter(i => i.severity !== "info").length,
    criticalCount: issues.filter(i => i.severity === "critical").length,
    majorCount:    issues.filter(i => i.severity === "major").length,
    healthScore:   computeHealthScore(issues),
    generatedAt:   Date.now(),
  };
}

/**
 * Quick check - is the project in a deployable state?
 */
export function isProjectDeployable(): boolean {
  const issues = runRuntimeMonitor();
  return !issues.some(i => i.severity === "critical");
}

/**
 * Get only auto-fixable issues - used by the self-healing engine.
 */
export function getAutoFixableIssues(): DetectedIssue[] {
  return runRuntimeMonitor().filter(i => i.autoFixable && i.severity !== "info");
}